import { SPRITE_SYMBOLS, type Sprite } from '../ui/data/spriteTypes';

/**
 * A sprite's pixels for the aquarium's fish (M83; their atlas since M88,
 * `fishBatch.ts`), its clear pixels given their drawn neighbors' color (M83's
 * review): left black, the filtering that shrinks a sprite blended that
 * black into its edge, a dark rim round every fish. Each clear pixel takes
 * the mean of its drawn neighbors' colors, spreading outward a pixel a pass
 * (`BLEED_PASSES`), its alpha still 0. (A fish facing right is turned over
 * in the batch's shader, M88; M83's review had given it a mirrored texture
 * of its own, as three.js's sprites lose a negative scale.)
 */

/** Passes of the bleed: enough for the coarsest level a sprite is shown at, its pixels 8 to a texel (JUDGED). */
const BLEED_PASSES = 8;

/** A sprite's RGBA, row by row from the top, its clear pixels bled. */
export function spriteRgba(sprite: Sprite): Uint8Array {
  const { width: w, height: h } = sprite;
  const colors = sprite.palette.map((hex) => [1, 3, 5].map((k) => parseInt(hex.slice(k, k + 2), 16)));
  const rgba = new Uint8Array(w * h * 4);
  sprite.rows.forEach((row, y) => [...row].forEach((symbol, x) => {
    if (symbol === '.') return;
    const color = colors[SPRITE_SYMBOLS.indexOf(symbol)] ?? [0, 0, 0];
    rgba.set([...color, 255], (y * w + x) * 4);
  }));
  // Clear pixels take their colored neighbors' mean, a ring a pass; their alpha stays 0.
  const colored = Array.from({ length: w * h }, (_, i) => rgba[i * 4 + 3] === 255);
  for (let pass = 0; pass < BLEED_PASSES; pass++) {
    const next = colored.slice();
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = y * w + x;
        if (colored[i]) continue;
        let [r, g, b, n] = [0, 0, 0, 0];
        for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
          const xx = x + dx;
          const yy = y + dy;
          if (xx < 0 || yy < 0 || xx >= w || yy >= h || !colored[yy * w + xx]) continue;
          const j = (yy * w + xx) * 4;
          r += rgba[j] as number;
          g += rgba[j + 1] as number;
          b += rgba[j + 2] as number;
          n++;
        }
        if (n === 0) continue;
        rgba.set([Math.round(r / n), Math.round(g / n), Math.round(b / n), 0], i * 4);
        next[i] = true;
      }
    }
    colored.splice(0, colored.length, ...next);
  }
  return rgba;
}
